import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useAuthStore from '../store/authStore';
import { adminLogin, requestPasswordRecovery } from '../api/auth';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../api/response';
import { canUseLocalTenantSelector, getRuntimeTenantId, setRuntimeTenantId } from '../api/tenantIdentity';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState(getRuntimeTenantId());
  const [recoverMode, setRecoverMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const showTenantSelector = canUseLocalTenantSelector();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (recoverMode) {
        setRuntimeTenantId(tenantId);
        await requestPasswordRecovery(email, tenantId);
        toast.success('Solicitud de recuperacion enviada');
        setRecoverMode(false);
        return;
      }

      setRuntimeTenantId(tenantId);
      const response = await adminLogin(email, password);
      
      if (response.user && response.token) {
        login(response.user, response.token);
        toast.success('¡Bienvenido!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al iniciar sesion'));
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="p-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">VeteSuite Admin</h1>
          <p className="mb-6 text-gray-600">
            {recoverMode ? 'Recupera el acceso asociado a la clinica.' : 'Panel administrativo para veterinarias'}
          </p>
          {tenantId ? (
            <p className="mb-6 rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800">
              Clinica conectada: {tenantId}
            </p>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 px-4 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="admin@clinica.cl"
              />
            </div>

            {recoverMode ? (
              showTenantSelector ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID de clinica
                  </label>
                  <input
                    type="text"
                    value={tenantId}
                    onChange={(e) => {
                      setTenantId(e.target.value);
                      setRuntimeTenantId(e.target.value);
                    }}
                    required
                    className="w-full rounded-md border border-gray-300 px-4 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="uuid o subdominio de tenant"
                  />
                </div>
              ) : null
            ) : (
              <>
                {showTenantSelector ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Clínica
                    </label>
                    <input
                      type="text"
                      value={tenantId}
                      onChange={(e) => {
                        setTenantId(e.target.value);
                        setRuntimeTenantId(e.target.value);
                      }}
                      className="w-full rounded-md border border-gray-300 px-4 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="fantasy, uuid o deja vacío si tu usuario es único"
                    />
                  </div>
                ) : null}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contraseña
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full rounded-md border border-gray-300 px-4 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="********"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-blue-600 py-2 font-medium text-white transition duration-200 hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Procesando...' : recoverMode ? 'Solicitar recuperacion' : 'Iniciar sesion'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => setRecoverMode((value) => !value)}
            className="mt-4 w-full text-sm font-medium text-blue-700 hover:text-blue-800"
          >
            {recoverMode ? 'Volver al inicio de sesion' : 'Recuperar contraseña'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
